import { forwardRef } from 'react';
import { motion } from 'framer-motion';

/**
 * Reusable Button component with consistent styling and multiple variants
 * 
 * @example
 * // Basic usage
 * <Button>Click me</Button>
 * 
 * @example
 * // With icon on the right
 * <Button variant="primary" icon={<ArrowIcon />}>
 *   Submit
 * </Button>
 * 
 * @example
 * // As a link
 * <Button href="/events" variant="secondary" size="lg">
 *   View Events
 * </Button>
 * 
 * @example
 * // Full width with loading state
 * <Button fullWidth loading variant="primary">
 *   Save Changes
 * </Button>
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Button content
 * @param {'primary'|'secondary'|'purple'|'outline'|'ghost'|'danger'} [props.variant='primary'] - Button style variant
 * @param {'xs'|'sm'|'md'|'lg'|'xl'} [props.size='md'] - Button size
 * @param {React.ReactNode} [props.icon] - Optional icon element
 * @param {'left'|'right'} [props.iconPosition='right'] - Icon position relative to text
 * @param {boolean} [props.fullWidth=false] - Whether button should take full width
 * @param {boolean} [props.disabled=false] - Whether button is disabled
 * @param {boolean} [props.loading=false] - Whether button shows loading state
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {Function} [props.onClick] - Click handler for button
 * @param {string} [props.href] - If provided, renders as anchor tag
 * @param {string} [props.target] - Target for anchor tag (e.g., '_blank')
 * @param {string} [props.rel] - Rel attribute for anchor tag
 * @param {string} [props.type='button'] - Button type
 * @param {Object} props - Additional props passed to button/anchor element
 */
const Button = forwardRef(({
    children,
    variant = 'primary',
    size = 'md',
    icon,
    iconPosition = 'right',
    fullWidth = false,
    disabled = false,
    loading = false,
    className = '',
    onClick,
    href,
    target,
    rel,
    type = 'button',
    ...props
}, ref) => {
    // Variant styles
    const variants = {
        primary: 'bg-green-100 text-white hover:bg-green-100/95 focus:ring-2 focus:ring-offset-2 focus:ring-green-200',
        secondary: 'bg-beige-200 text-brown-200 hover:bg-beige-200/80 focus:ring-2 focus:ring-offset-2 focus:ring-beige-300',
        purple: 'bg-purple-100 text-white hover:bg-purple-100/95 focus:ring-2 focus:ring-offset-2 focus:ring-purple-200',
        outline: 'bg-transparent border border-beige-200 text-brown-200 hover:bg-beige-50 focus:ring-2 focus:ring-offset-2 focus:ring-beige-300',
        ghost: 'bg-transparent text-brown-200 hover:bg-beige-50 focus:ring-2 focus:ring-offset-2 focus:ring-beige-300',
        danger: 'bg-red-100 text-white hover:bg-red-100/95 focus:ring-2 focus:ring-offset-2 focus:ring-red-200'
    };

    // Size styles
    const sizes = {
        xs: 'px-2 py-1 text-xs',
        sm: 'px-3 py-2 text-sm',
        md: 'px-4 py-2 text-sm',
        lg: 'px-5 py-3 text-xs',
        xl: 'px-6 py-4 text-base'
    };

    // Font weight based on variant
    const fontWeights = {
        primary: 'font-black',
        secondary: 'font-black',
        purple: 'font-black',
        outline: 'font-bold',
        ghost: 'font-bold',
        danger: 'font-black'
    };

    const baseClasses = `
        inline-flex items-center justify-center gap-2
        rounded
        focus:outline-none
        transition-colors duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
        ${variants[variant]}
        ${sizes[size]}
        ${fontWeights[variant]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
    `.replace(/\s+/g, ' ').trim();

    const content = (
        <>
            {icon && iconPosition === 'left' && (
                <span className="flex-shrink-0">
                    {icon}
                </span>
            )}
            {loading ? (
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    <span>Loading...</span>
                </div>
            ) : (
                children
            )}
            {icon && iconPosition === 'right' && (
                <span className="flex-shrink-0">
                    {icon}
                </span>
            )}
        </>
    );

    // If href is provided, render as a link
    if (href) {
        return (
            <motion.a
                ref={ref}
                href={href}
                target={target}
                rel={rel}
                className={baseClasses}
                {...props}
            >
                {content}
            </motion.a>
        );
    }

    // Render as button
    return (
        <motion.button
            ref={ref}
            type={type}
            className={baseClasses}
            onClick={onClick}
            disabled={disabled || loading}
            {...props}
        >
            {content}
        </motion.button>
    );
});

Button.displayName = 'Button';

export default Button;
